import React from "react";
import { MapPin, Phone, Mail, Clock, Send, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
const SHARED_DATA = {
  address: "London, UK",
  phone: "+44 20 7946 0123",
  email: "info@liveamazing.uk",
  hours: "Mon-Fri: 9am-6pm"
};
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(({
  className,
  type,
  ...props
}, ref) => {
  return <input type={type} className={cn("border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex h-12 w-full rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm", className)} ref={ref} {...props} />;
});
Input.displayName = "Input";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 bg-white/5 rounded-xl border border-white/10 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-green-500/20 flex items-center justify-center mb-6">
          <Send className="size-8 text-green-500" />
        </div>
        <h3 className="text-2xl font-serif text-white mb-2">Message Sent</h3>
        <p className="text-gray-400 max-w-md">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-gray-300">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-white/5 border-white/10 text-white focus-visible:border-yellow-500/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-gray-300">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-white/5 border-white/10 text-white focus-visible:border-yellow-500/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-gray-300">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 7123 456789" {...field} className="bg-white/5 border-white/10 text-white focus-visible:border-yellow-500/50" />
              </FormControl>
              <FormDescription className="text-gray-500">Optional – we may use this to call you back</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-gray-300">Subject</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Rental inquiry" {...field} className="bg-white/5 border-white/10 text-white focus-visible:border-yellow-500/50" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-gray-300">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us how we can help..." className="min-h-[150px] bg-white/5 border-white/10 text-white focus-visible:border-yellow-500/50 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base bg-white text-black hover:bg-gray-200 font-medium transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Message...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function StartAConversation() {
  return <section id="start-a-conversation" className="relative w-full bg-[#1C1C1C] text-white overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPgo8cmVjdCB3aWR0aD0iNCIgaGVpZ2h0PSI0IiBmaWxsPSIjZmZmIi8+CjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiMwMDAiLz4KPC9zdmc+')]"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[800px]">
          {}
          <div className="relative flex flex-col justify-between py-16 lg:py-24 lg:pr-16 border-b lg:border-b-0 lg:border-r border-white/10">
            <div className="space-y-8 relative z-10">
              <div>
                <h2 className="text-4xl md:text-5xl font-serif font-medium mb-6 tracking-tight">
                  Start a <span className="text-yellow-400 italic">Conversation</span>
                </h2>
                <p className="text-lg text-gray-400 max-w-md leading-relaxed">
                  Whether you're a landlord looking to maximize your property's potential or a tenant searching for your next home, we're here to help.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4 group">
                  <div className="size-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-yellow-500/50 transition-colors">
                    <MapPin className="size-5 text-gray-300 group-hover:text-yellow-400 transition-colors" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1">Visit Us</h4>
                    <p className="text-lg font-medium">94 Goldhawk Road, W128HD, London, UK</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="size-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-yellow-500/50 transition-colors">
                    <Phone className="size-5 text-gray-300 group-hover:text-yellow-400 transition-colors" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1">Call Us</h4>
                    <div className="flex flex-col items-start gap-2">
                      <p className="text-lg font-medium">+44 7542683501</p>
                      <Button variant="outline" size="sm" className="h-8 text-xs border-white/20 bg-transparent hover:bg-white/10 hover:text-white text-gray-300" asChild>
                        <Link to={`tel:${SHARED_DATA.phone}`}>
                          Call Now
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="size-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-yellow-500/50 transition-colors">
                    <Mail className="size-5 text-gray-300 group-hover:text-yellow-400 transition-colors" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1">Email Us</h4>
                    <p className="text-lg font-medium">{SHARED_DATA.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="size-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:border-yellow-500/50 transition-colors">
                    <Clock className="size-5 text-gray-300 group-hover:text-yellow-400 transition-colors" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1">Opening Hours</h4>
                    <p className="text-lg font-medium">{SHARED_DATA.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="mt-12 h-64 w-full rounded-xl overflow-hidden border border-white/10 relative group">
              <div className="absolute inset-0 bg-gray-900 z-0">
                {}
                <iframe title="Office Location" width="100%" height="100%" frameBorder="0" scrolling="no" marginHeight={0} marginWidth={0} src="https://maps.google.com/maps?q=London%2C%20UK&t=&z=13&ie=UTF8&iwloc=&output=embed" className="w-full h-full opacity-80 hover:opacity-100 transition-opacity duration-500" style={{
                filter: 'invert(90%) hue-rotate(180deg) contrast(90%) grayscale(20%)'
              }}></iframe>
              </div>
              <div className="absolute bottom-4 left-4 z-10">
                <Button size="sm" className="bg-white text-black hover:bg-gray-200 shadow-lg" asChild>
                  
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="relative py-16 lg:py-24 lg:pl-16 flex flex-col justify-center">
            <div className="bg-[#252525] border border-white/5 p-8 md:p-10 rounded-2xl shadow-2xl relative overflow-hidden">
              {}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500/50 to-transparent"></div>
              
              <div className="mb-8">
                <h3 className="text-2xl font-serif text-white mb-2">Send a Message</h3>
                <p className="text-gray-400 text-sm">
                  Fill out the form below and our team will get back to you within 24 hours.
                </p>
              </div>

              <FormProvider formId="start_conversation_form_contact" sectionName="contact-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true
              }
            }} submitText="Send Message" successMessage="Thank you! Your message has been sent successfully. We will be in touch shortly.">
                <ContactFormContent />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}